import type { AskYogiChartResponse } from 'features/ask-yogi/types'
import type { ChartValue } from 'features/charts-v2/types'

/**
 * The rows an artifact chart should draw: the grouped ones when they exist.
 *
 * The engine cannot group in SQL. A grouped brand / product / theme axis is
 * lowered to its leaf column, queried, and rolled up afterwards — the rollup
 * lands on `parent_data` while the leaf rows stay on `data`. Drawing `data`
 * therefore renders five brand lines for a chart whose panel asked for "brand
 * group", which is the bug this exists to close.
 *
 * Presence is the whole test, and it matches the backend deliberately. The
 * grouping pipeline says so in as many words — *"`is not None`, never
 * truthiness: an empty grouped result is a real result"* — and `to_table` now
 * refuses rather than falling back when a grouped request has no grouped rows.
 * An earlier version here added `&& length > 0`, which is the one clause that
 * argument does not cover: it would render five brand lines for a grouped
 * request that rolled up to nothing, reintroducing in the chart exactly the
 * table/chart mismatch this series of commits closes. An empty chart reads as
 * "no data"; five ungrouped lines read as an answer.
 *
 * Deliberately NOT `selectGroupedChartData`, which the charts page uses. That
 * helper decides from the breakdowns' own `grouped` flag, and an artifact's
 * breakdowns are derived from `identifier.domains` — already rewritten to the
 * leaf dimension by the time the client sees them, so the flag is gone. It
 * also carries a branch for grouping being toggled OFF client-side without a
 * refetch; an artifact has no such toggle, so its rows are settled when it is
 * drawn.
 *
 * Absent rows come back as `[]`, and that is not defensive padding: `data` is
 * typed `ChartValue[]` but the backend can send null, so the type is what
 * lies. The call site this replaced carried the check — `!chartResponse.data
 * || chartResponse.data.length === 0` — and folding it in here lost the null
 * half, which made a null `data` throw where it used to report `empty`. Both
 * call sites read `.length` or map over the result, so the total function is
 * what the return type already promises.
 */
export function groupedRowsOrLeaves(
  response: Pick<AskYogiChartResponse, 'data' | 'parent_data'>,
): ChartValue[] {
  const { parent_data: parentData } = response
  if (parentData != null) return parentData
  return response.data ?? []
}
